import type { CompanionAd, MediaFile, ParsedAd, TrackingEvent, VastEvent } from './types.js';

/**
 * Minimal VAST 4.0 InLine parser. Handles the subset our ad-server emits
 * (Linear + Companions + UniversalAdId). Does NOT chase Wrappers — the
 * ad-server resolves those server-side and the player only sees the
 * final InLine. AdPod is exposed as an ordered list of ParsedAd.
 */
export function parseVast(xml: string): ParsedAd[] {
  if (!xml || typeof xml !== 'string' || xml.trim().length === 0) {
    throw new Error('VAST parse error: empty input');
  }
  // Preprocess CDATA sections into entity-escaped text so .textContent works
  // uniformly across real browsers, jsdom, and happy-dom (the latter drops
  // CDATA content silently).
  const normalised = xml.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (_, content) =>
    String(content)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;'),
  );
  const doc = new DOMParser().parseFromString(normalised, 'text/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) throw new Error('VAST parse error: ' + (parserError.textContent ?? 'unknown'));
  // Sanity: a valid VAST root must exist.
  if (!doc.querySelector('VAST')) {
    throw new Error('VAST parse error: no <VAST> root');
  }

  const ads = Array.from(doc.querySelectorAll('Ad'));
  return ads
    .map((adEl) => parseAd(adEl))
    .filter((a): a is ParsedAd => a !== null)
    // AdPod ordering: sort by sequence ASC if provided
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

function parseAd(adEl: Element): ParsedAd | null {
  // Prefer InLine, fall back to Wrapper (rare in our pipeline but be lenient).
  const inline = adEl.querySelector('InLine') ?? adEl.querySelector('Wrapper');
  if (!inline) return null;

  const id = adEl.getAttribute('id') ?? undefined;
  const seqRaw = adEl.getAttribute('sequence');
  const sequence = seqRaw ? parseInt(seqRaw, 10) : undefined;

  const impressionUrls = textArray(inline, 'Impression');
  const errorUrls = textArray(inline, 'Error');

  // Find the first Linear creative (we don't handle NonLinearAds currently).
  const linear = inline.querySelector('Creatives > Creative > Linear');
  const durationText = linear?.querySelector('Duration')?.textContent ?? '';
  const durationSec = parseDuration(durationText.trim());

  const mediaFiles = linear
    ? Array.from(linear.querySelectorAll('MediaFiles > MediaFile')).map((m) => parseMediaFile(m))
    : [];

  const trackingEvents = linear
    ? Array.from(linear.querySelectorAll('TrackingEvents > Tracking')).map((t) => parseTracking(t))
    : [];

  const clickThrough = linear?.querySelector('VideoClicks > ClickThrough')?.textContent?.trim() || undefined;
  const clickTracking = linear
    ? textArray(linear, 'VideoClicks > ClickTracking')
    : [];

  const companions = Array.from(inline.querySelectorAll('Creatives > Creative > CompanionAds > Companion'))
    .map((c) => parseCompanion(c));

  const uniIdEl = inline.querySelector('Creatives > Creative > UniversalAdId');
  let universalAdId: ParsedAd['universalAdId'];
  if (uniIdEl) {
    const value = (uniIdEl.textContent ?? '').trim();
    const reg = uniIdEl.getAttribute('idRegistry');
    universalAdId = reg ? { value, registry: reg } : { value };
  }

  const out: ParsedAd = {
    mediaFiles,
    trackingEvents,
    clickTracking,
    impressionUrls,
    errorUrls,
    companions,
  };
  if (id) out.id = id;
  if (sequence !== undefined) out.sequence = sequence;
  if (durationSec !== undefined) out.durationSec = durationSec;
  if (clickThrough) out.clickThrough = clickThrough;
  if (universalAdId) out.universalAdId = universalAdId;
  return out;
}

function parseMediaFile(el: Element): MediaFile {
  const m: MediaFile = {
    url: (el.textContent ?? '').trim(),
    type: el.getAttribute('type') ?? 'video/mp4',
  };
  const w = intAttr(el, 'width');
  const h = intAttr(el, 'height');
  const br = intAttr(el, 'bitrate');
  const del = el.getAttribute('delivery');
  if (w !== undefined) m.width = w;
  if (h !== undefined) m.height = h;
  if (br !== undefined) m.bitrate = br;
  if (del) m.delivery = del;
  return m;
}

function parseTracking(el: Element): TrackingEvent {
  return {
    event: (el.getAttribute('event') ?? 'progress') as VastEvent,
    url: (el.textContent ?? '').trim(),
  };
}

function parseCompanion(el: Element): CompanionAd {
  const width = intAttr(el, 'width') ?? 0;
  const height = intAttr(el, 'height') ?? 0;
  const staticRes = el.querySelector('StaticResource');
  const iframeRes = el.querySelector('IFrameResource');
  const htmlRes = el.querySelector('HTMLResource');
  const type = staticRes?.getAttribute('creativeType') ?? (iframeRes ? 'iframe' : htmlRes ? 'text/html' : '');
  const resourceUrl = (staticRes?.textContent ?? iframeRes?.textContent ?? '').trim();
  const htmlContent = htmlRes ? (htmlRes.textContent ?? '').trim() : '';
  const clickThrough = el.querySelector('CompanionClickThrough')?.textContent?.trim() ?? '';
  const c: CompanionAd = {
    width,
    height,
    clickTracking: textArray(el, 'CompanionClickTracking'),
    trackingEvents: Array.from(el.querySelectorAll('TrackingEvents > Tracking')).map((t) => parseTracking(t)),
  };
  if (type) c.type = type;
  if (resourceUrl) c.resourceUrl = resourceUrl;
  if (htmlContent) c.htmlContent = htmlContent;
  if (clickThrough) c.clickThrough = clickThrough;
  return c;
}

/**
 * Pick the best MediaFile for a given environment. Strategy:
 *   1. Prefer progressive video/mp4 within target size & bitrate.
 *   2. Fall back to HLS (m3u8) — caller can dynamically load hls.js.
 *   3. Fall back to anything playable.
 */
export function selectMedia(
  mediaFiles: MediaFile[],
  opts: { maxWidth?: number; maxBitrate?: number } = {},
): MediaFile | null {
  if (mediaFiles.length === 0) return null;
  const maxW = opts.maxWidth ?? Number.POSITIVE_INFINITY;
  const maxBr = opts.maxBitrate ?? Number.POSITIVE_INFINITY;

  const candidates = mediaFiles.filter((m) => m.url.length > 0);
  if (candidates.length === 0) return null;

  const score = (m: MediaFile): number => {
    let s = 0;
    if (/video\/mp4/i.test(m.type)) s += 1000;
    else if (/x-mpegurl|hls/i.test(m.type)) s += 500;
    else if (/dash\+xml/i.test(m.type)) s += 200;
    else s += 50;
    const w = m.width ?? 0;
    const br = m.bitrate ?? 0;
    if (w <= maxW) s += Math.min(w, 1920) / 10;
    else s -= 200;
    if (br <= maxBr) s += Math.min(br, 5000) / 100;
    return s;
  };

  return candidates.slice().sort((a, b) => score(b) - score(a))[0]!;
}

function textArray(root: ParentNode, selector: string): string[] {
  return Array.from(root.querySelectorAll(selector))
    .map((el) => (el.textContent ?? '').trim())
    .filter((s) => s.length > 0);
}

function intAttr(el: Element, name: string): number | undefined {
  const v = el.getAttribute(name);
  if (!v) return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse HH:MM:SS(.mmm) into seconds. Returns undefined when malformed. */
export function parseDuration(s: string): number | undefined {
  if (!s) return undefined;
  const m = s.match(/^(\d+):(\d{2}):(\d{2})(?:\.(\d+))?$/);
  if (!m) {
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  const h = m[1] ?? '0';
  const mm = m[2] ?? '0';
  const ss = m[3] ?? '0';
  const frac = m[4];
  const base = parseInt(h, 10) * 3600 + parseInt(mm, 10) * 60 + parseInt(ss, 10);
  const fracN = frac ? parseFloat('0.' + frac) : 0;
  return base + fracN;
}
